import type { AssignmentTemplate, AssignmentTemplatePayload } from '../../model/assignments/interfaces'
import type { ApiResponse } from '@shared/api'

import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface AssignmentTemplateStoreApiOptions {
  /** From `buildSavePayload()` — never Zod's parsed output, which strips unknown keys. */
  payload: AssignmentTemplatePayload
  onStart?: () => void
  onSuccess?: (template: AssignmentTemplate) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `POST /admin-api/assignment-templates`. Answers `201` with the same body as `show`. `data` must be
 *  non-empty (`{}` is a 422). */
export const useAssignmentTemplateStoreApi = async (
  options: AssignmentTemplateStoreApiOptions,
): Promise<AssignmentTemplate | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.assignment-templates.store')

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, options.payload)

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return null
    }

    const template: AssignmentTemplate | null = data.data?.template ?? null

    // A `201` with no template is a broken contract.
    if (!template) {
      const msg = 'Unexpected error. The assignment template is missing from the response.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    toast.success('Assignment template created')
    options.onSuccess?.(template)
    return template
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)
    return null
  } finally {
    commonStore.setLoading(false)
    options.onFinish?.()
  }
}
